import React, { useEffect } from 'react';
import { useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import {
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';
import {
  Form,
  Link,
  redirect,
  useActionData,
  useLoaderData,
  useNavigation,
} from 'react-router-dom';
import customFetch from '../../utils/customFetch';
import { useCartContext } from '../../context/cartContext';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-toastify';
import CheckoutForm from './CheckoutForm';

export const loader = async () => {
  try {
    const userData = await customFetch.get('/users/current-user');
    const userAddress = await customFetch.get('/address');
    return {
      userData: userData || { user: null },
      userAddress: userAddress || { address: null },
    };
  } catch (error) {
    return { userData: { user: null }, userAddress: { address: null } };
  }
};

export const action = async ({ request }) => {
  const formData = await request.formData();
  const data = Object.fromEntries(formData);
  const cart = JSON.parse(data.cart);

  const orderObject = {
    tax: data.tax,
    shippingFee: data.shippingFee,
    cartItems: cart,
    address: {
      firstName: data.firstName,
      lastName: data.lastName,
      company: data.company,
      address: data.address,
      city: data.city,
      zip: data.zip,
      country: data.country,
      phone: data.phone,
    },
  };

  try {
    const response = await customFetch.post('/orders', orderObject);
    return response;
  } catch (error) {
    toast.error(error?.response?.data?.msg);
    return error;
  }
};

const deliveryMethods = [
  {
    id: 1,
    title: 'PostPac Economy',
    turnaround: 'innerhalb von 2 Werktagen nach Versand',
    price: 500,
    default: true,
  },
  {
    id: 2,
    title: 'PostPac Priority',
    turnaround: 'am nächsten Werktag nach Versand',
    price: 1000,
    default: false,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const stripePromise = loadStripe(
  process.env.NODE_ENV === 'development'
    ? 'pk_test_51Q11IA03eRWCkAXaR943czFWTGt8alJreLN4vPfUYGxc8Y4gpADTQgZvI26o8CToOX0ZruaM2IxajdSZwAKhNWZ000F6oclYl7'
    : 'pk_live_51Q11IA03eRWCkAXa7ODLDX23LlAew0SdYDlgn6wDxTCUT8qMMzwiKG8Bk6vIN6oupytjzoYg6pTJi9iKzlBd9wwy00S2Nxnv49'
);

const OrderPage = () => {
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(
    deliveryMethods[0].price
  );

  const [address, setUserAddress] = useState([]);
  const [orderId, setOrderId] = useState();
  const { userData, userAddress } = useLoaderData();
  const { cart, total_amount, updateShipping } = useCartContext();

  // User from local storage
  const userObject = localStorage.getItem('user');
  let user;
  if (userObject) {
    user = JSON.parse(userObject);
    if (user.timestamp) {
      const storedTimestamp = new Date(user.timestamp);
      const currentTime = new Date();

      const timeDifference = currentTime - storedTimestamp;

      const oneDayInMs = 24 * 60 * 60 * 1000;

      if (timeDifference < oneDayInMs) {
        user.ok = true;
      } else {
        user.ok = false;
      }
    } else {
      console.log('Kein Timestamp gefunden.');
    }
  }

  useEffect(() => {
    updateShipping(selectedDeliveryMethod);
  }, [selectedDeliveryMethod]);

  useEffect(() => {
    if (userData && userData.data) {
      setUserAddress(userAddress.data.userAddress[0]);
    }
  }, []);

  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const [clientSecret, setClientSecret] = useState('');
  const actionData = useActionData();
  useEffect(() => {
    if (actionData) {
      setClientSecret(actionData.data.clientSecret);
      console.log(actionData);
      setOrderId(actionData.data.order._id);
    }
  }, [actionData]);

  const appearance = {
    theme: 'stripe',
  };
  const options = {
    clientSecret,
    appearance,
  };

  return (
    <main className="py-24">
      <div className="px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
          Bestellung
        </h1>
      </div>

      <div className="mx-auto max-w-2xl px-4 pb-24 pt-16 sm:px-6 lg:max-w-7xl lg:px-8">
        <h2 className="sr-only">Bestellung</h2>
        {/* Hier Case Switch wenn Bezahlung initiiert */}
        {clientSecret ? (
          <Elements options={options} stripe={stripePromise}>
            <CheckoutForm orderId={orderId} />
          </Elements>
        ) : (
          <>
            {/* Anmeldung wenn schon Konto, weglassen wenn User vorhanden*/}
            {user?.ok === true ? (
              ''
            ) : (
              <div className="mb-4">
                <div className="rounded-md bg-blue-50 p-4 mt-4">
                  <div className="flex">
                    <div className="shrink-0">
                      <InformationCircleIcon
                        className="h-5 w-5 text-blue-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3 flex-1 md:flex md:justify-between">
                      <p className="text-sm text-blue-700">
                        Hast du schon ein Konto? Dann melde dich hier an, damit
                        du deine Bestellung abgeben kannst. Sonst kannst du
                        einfach ein neues Konto erstellen.
                      </p>
                      <p className="mt-3 text-sm md:ml-6 md:mt-0">
                        <Link
                          to="/login?redirectURL=/warenkorb/bestellung"
                          className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600"
                        >
                          Einloggen
                          <span aria-hidden="true"> &rarr;</span>
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Form
              method="POST"
              className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16"
            >
              <div
                className={`${
                  user?.ok === true ? '' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <h2 className="text-lg font-medium text-gray-900">
                  Lieferadresse
                </h2>

                <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Vorname
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        autoComplete="given-name"
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm disabled:cursor-not-allowed"
                        defaultValue={address?.firstName || ''}
                        disabled={user?.ok === false}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Nachname
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        autoComplete="family-name"
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm disabled:cursor-not-allowed"
                        defaultValue={address?.lastName || ''}
                        disabled={user?.ok === false}
                        required
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Firma (optional)
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="company"
                        id="company"
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm disabled:cursor-not-allowed"
                        disabled={user?.ok === false}
                        defaultValue={address?.company || ''}
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor="address"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Adresse
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="address"
                        id="address"
                        autoComplete="street-address"
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm disabled:cursor-not-allowed"
                        disabled={user?.ok === false}
                        defaultValue={address?.address || ''}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Stadt
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="city"
                        id="city"
                        autoComplete="address-level2"
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm disabled:cursor-not-allowed"
                        disabled={user?.ok === false}
                        defaultValue={address?.city || ''}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="country"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Land
                    </label>
                    <div className="mt-1">
                      <select
                        id="country"
                        name="country"
                        autoComplete="country-name"
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm"
                        required
                      >
                        <option>Schweiz</option>
                        {/* <option>Deutschland</option>
                      <option>Österreich</option> */}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="zip"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Postleitzahl
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="zip"
                        id="zip"
                        autoComplete="postal-code"
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm disabled:cursor-not-allowed"
                        disabled={user?.ok === false}
                        defaultValue={address?.zip || ''}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Telefonnummer
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="phone"
                        id="phone"
                        autoComplete="tel"
                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm disabled:cursor-not-allowed"
                        disabled={user?.ok === false}
                        defaultValue={address?.phone || ''}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-10 border-t border-gray-200 pt-10">
                  <RadioGroup
                    name="shippingFee"
                    value={selectedDeliveryMethod}
                    onChange={setSelectedDeliveryMethod}
                  >
                    <RadioGroup.Label className="text-lg font-medium text-gray-900">
                      Liefermethode
                    </RadioGroup.Label>

                    <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                      {deliveryMethods.map((deliveryMethod) => (
                        <RadioGroup.Option
                          key={deliveryMethod.id}
                          value={deliveryMethod.price}
                          className={({ checked, active }) =>
                            classNames(
                              checked
                                ? 'border-transparent'
                                : 'border-gray-300',
                              active ? 'ring-2 ring-svisit-primary-500' : '',
                              'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-xs focus:outline-hidden'
                            )
                          }
                        >
                          {({ checked, active }) => (
                            <>
                              <span className="flex flex-1">
                                <span className="flex flex-col">
                                  <RadioGroup.Label
                                    as="span"
                                    className="block text-sm font-medium text-gray-900"
                                  >
                                    {deliveryMethod.title}
                                  </RadioGroup.Label>
                                  <RadioGroup.Description
                                    as="span"
                                    className="mt-1 flex items-center text-sm text-gray-500"
                                  >
                                    {deliveryMethod.turnaround}
                                  </RadioGroup.Description>
                                  <RadioGroup.Description
                                    as="span"
                                    className="mt-6 text-sm font-medium text-gray-900"
                                  >
                                    CHF {deliveryMethod.price / 100}
                                  </RadioGroup.Description>
                                </span>
                              </span>
                              {checked ? (
                                <CheckCircleIcon
                                  className="h-5 w-5 text-svisit-primary-600"
                                  aria-hidden="true"
                                />
                              ) : null}
                              <span
                                className={classNames(
                                  active ? 'border' : 'border-2',
                                  checked
                                    ? 'border-svisit-primary-500'
                                    : 'border-transparent',
                                  'pointer-events-none absolute -inset-px rounded-lg'
                                )}
                                aria-hidden="true"
                              />
                            </>
                          )}
                        </RadioGroup.Option>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* Zusammenfassung */}
              <div className="mt-10 lg:mt-0">
                <h2 className="text-lg font-medium text-gray-900">
                  Zusammenfassung
                </h2>

                <div className="mt-4 rounded-lg border border-gray-200 bg-white shadow-xs">
                  <h3 className="sr-only">Items in your cart</h3>
                  <ul role="list" className="divide-y divide-gray-200">
                    {cart.map((product) => (
                      <li key={product.id} className="flex px-4 py-6 sm:px-6">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="h-20 w-20 flex-none rounded-md object-cover object-center"
                        />
                        <div className="flex-auto space-y-1 ml-3 text-sm font-medium">
                          <h3>{product.name}</h3>
                        </div>
                        <p className="flex-none text-base font-medium">
                          CHF {product.price / 100}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <dl className="space-y-6 border-t border-gray-200 px-4 py-6 sm:px-6">
                    <div className="flex items-center justify-between">
                      <dt className="text-sm">Zwischentotal</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        CHF {Math.round(total_amount) / 100}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-sm">Lieferkosten</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        CHF {selectedDeliveryMethod / 100}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-sm">Enthaltene Mehrwertsteuer</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        CHF{' '}
                        {Math.round(
                          (total_amount + selectedDeliveryMethod) * 0.081
                        ) / 100}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                      <dt className="text-base font-medium">Gesamtbetrag</dt>
                      <dd className="text-base font-medium text-gray-900">
                        CHF {(total_amount + selectedDeliveryMethod) / 100}
                      </dd>
                    </div>
                  </dl>

                  <input
                    type="hidden"
                    name="tax"
                    id="tax"
                    value={Math.round(
                      (total_amount + selectedDeliveryMethod) * 0.081
                    )}
                  />

                  <input
                    type="hidden"
                    name="cart"
                    id="cart"
                    value={JSON.stringify(cart)}
                  />

                  <div className="border-t border-gray-200 px-4 py-6 sm:px-6">
                    <button
                      type="submit"
                      className="w-full rounded-md border border-transparent bg-svisit-primary-600 px-4 py-3 text-base font-medium text-white shadow-xs hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      disabled={isSubmitting || user?.ok === false}
                    >
                      {isSubmitting
                        ? 'Du wirst gleich weitergeleitet...'
                        : 'Bestellen und zur Zahlung'}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          </>
        )}
      </div>
    </main>
  );
};

export default OrderPage;
